import React, { useState } from 'react';
import { Field, change } from 'redux-form';
import TableRow from '@material-ui/core/TableRow';
import Fields from 'components/Fields';
import StoreProvider from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import TableComponents from 'components/Table';
import { policyActionHelper } from 'utils/policyActionHelper';
import Router from 'components/Router/Router';
import { Modal } from 'components/Base';
import ActionModal from './ActionModal';
import { ActionFieldsRenderer } from './ActionFieldRenderer';
import Icons from 'components/Icons';
import {
  formatCurrency,
  unFormatCurrency,
  formatPercent,
  toFixed,
  errHelper
} from 'utils';
import { CustomerType } from 'store/modules/Customers/types';

interface PolicyTypes {
  meta?: any;
  policy: any;
  policyTypes: MetaType;
  policyProviders: MetaType;
  policyPlatforms: MetaType;
  policyId: number;
  isProcessed: boolean;
  transferValue: string;
  performedAction: number;
  morningstarRiskAnalysis: any;
  getRecommendationsData: any;
  changeField: any;
  customers: Array<CustomerType>;
  policyTypesRecommended: MetaType;
  isEvalue: boolean;
}
interface ModalProps {
  isOpen: boolean;
  title: string | null;
  actionType: any;
}

const { connect, selectors, actions } = StoreProvider;
const { useParams } = Router;

export const policyActions = [
  { name: 'Retain no changes', value: 1 },
  { name: 'Retain rebalance', value: 2 },
  { name: 'Retain internal fund switch', value: 3 },
  { name: 'Transfer cash - full', value: 4 },
  { name: 'Transfer cash - partial', value: 5 },
  { name: 'Transfer re-register - full', value: 6 },
  { name: 'Transfer re-register - partial', value: 7 },
  { name: 'Surrender - partial', value: 8 },
  { name: 'Surrender - full', value: 9 }
];

const Policy = ({
  policy,
  policyId,
  policyTypes,
  policyProviders,
  policyPlatforms,
  isProcessed,
  transferValue,
  getRecommendationsData,
  performedAction,
  changeField,
  customers,
  policyTypesRecommended,
  isEvalue,
  morningstarRiskAnalysis
}: PolicyTypes) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modalData, setModalData] = useState<ModalProps>({
    isOpen: false,
    title: null,
    actionType: null
  });

  const performedActionName =
    performedAction &&
    policyActions.find((action: any) => action.value === performedAction);

  const { portfolio } = useParams() as any;
  const policyActionCall = (
    policyActionMethod: any,
    policyId: number,
    split?: any
  ) => {
    setIsLoading(true);

    return policyActionMethod({
      portfolio,
      existing_policy: policyId,
      split
    })
      .then(() => {
        return getRecommendationsData({ portfolio }).then(() => {
          setIsLoading(false);
          setModalData({
            isOpen: false,
            title: null,
            actionType: null
          });
        });
      })
      .catch((err: any) => {
        errHelper(err);
        setIsLoading(false);
      });
  };

  const actionWithUserAction = (policyActionType: any) => {
    const actionTitle = policyActions.find(
      (policyType: any) => policyType.value === policyActionType
    );

    setModalData({
      isOpen: true,
      title: actionTitle ? actionTitle.name : null,
      actionType: policyActionType
    });
  };

  const submitPartialsActions = (val: any) => {
    const { actionType, retained, surrendered, owner, type } = val;

    const prepareSplit = (
      actionType: string,
      retained: any,
      surrendered: any,
      transferValue: string
    ) => {
      // Action type Surrender - partial
      if (Number(actionType) === 8) {
        return {
          retained: toFixed(
            Number(transferValue) - Number(unFormatCurrency(surrendered))
          ),
          surrendered: toFixed(unFormatCurrency(surrendered))
        };
      }

      return {
        unallocated: toFixed(unFormatCurrency(retained)),
        retained: toFixed(
          Number(transferValue) - Number(unFormatCurrency(retained))
        )
      };
    };

    const split: any = prepareSplit(
      actionType,
      retained,
      surrendered,
      transferValue
    );

    if (owner && type) {
      const policy = {
        type: type,
        owner: {
          id: owner
        }
      };
      split.policy = policy;
    }

    const policyActionMethod = policyActionHelper(actionType);

    return policyActionCall(policyActionMethod, policyId, split);
  };

  const actionWithoutUserAction = (policyActionType: any, policyId: number) => {
    const policyActionMethod = policyActionHelper(policyActionType);

    policyActionCall(policyActionMethod, policyId);
  };

  const closeActionModal = () => {
    changeField('recommendations', `${policy}.isProcessed`, false);
    setModalData({
      isOpen: false,
      title: null,
      actionType: null
    });
  };

  return (
    <TableRow>
      <Modal isOpen={modalData.isOpen} closeModalFunc={closeActionModal}>
        <ActionModal
          onSubmit={submitPartialsActions}
          close={closeActionModal}
          initialValues={{
            title: modalData.title,
            actionType: modalData.actionType,
            closeModalFunc: () => {
              changeField('recommendations', `${policy}.isProcessed`, false);
              setModalData({
                isOpen: false,
                title: null,
                actionType: null
              });
            }
          }}
        >
          {ActionFieldsRenderer(
            modalData.actionType,
            transferValue,
            policyTypesRecommended,
            customers
          )}
        </ActionModal>
      </Modal>
      <TableComponents.TableCell width="150px">
        <Field name={`${policy}.owner`} fullWidth component={Fields.String} />
      </TableComponents.TableCell>

      <TableComponents.TableCell>
        <Field
          name={`${policy}.platform`}
          fullWidth
          disabled
          options={policyPlatforms}
          component={Fields.String}
        />
      </TableComponents.TableCell>

      <TableComponents.TableCell>
        <Field
          name={`${policy}.provider`}
          fullWidth
          displayEmpty
          options={policyProviders}
          component={Fields.String}
        />
      </TableComponents.TableCell>

      <TableComponents.TableCell>
        <Field
          name={`${policy}.type`}
          fullWidth
          displayEmpty
          options={policyTypes}
          component={Fields.String}
        />
      </TableComponents.TableCell>

      <TableComponents.TableCell>
        <Field
          name={`${policy}.fund_value`}
          fullWidth
          format={(val: any) => formatCurrency(val)}
          component={Fields.String}
        />
      </TableComponents.TableCell>

      <TableComponents.TableCell>
        <Field
          name={`${policy}.transfer_value`}
          fullWidth
          format={(val: any) => formatCurrency(val)}
          component={Fields.String}
        />
      </TableComponents.TableCell>
      <TableComponents.TableCell>
        <Field
          name={`${policy}.total_ongoing_charge`}
          fullWidth
          type="number"
          format={(val: any) => {
            if (!val) return '-';
            return formatPercent(val);
          }}
          validate={Fields.Validators.required}
          component={Fields.String}
        />
      </TableComponents.TableCell>
      {isEvalue ? (
        <TableComponents.TableCell>
          <Field
            name={`${policy}.risk_score`}
            fullWidth
            type="number"
            format={(val: any) => {
              if (!val) return '-';
              return val;
            }}
            validate={Fields.Validators.required}
            component={Fields.String}
          />
        </TableComponents.TableCell>
      ) : (
        <>
          <TableComponents.TableCell>
            {formatPercent(morningstarRiskAnalysis.growth_score)}
          </TableComponents.TableCell>
          <TableComponents.TableCell>
            {`${morningstarRiskAnalysis.range?.low} - ${morningstarRiskAnalysis.range?.high}`}
          </TableComponents.TableCell>
        </>
      )}
      <TableComponents.TableCell width="250px">
        {isProcessed || isLoading ? (
          <Icons.LoadingWrapper isLoading={isLoading}>
            <span>{performedActionName && performedActionName.name}</span>
          </Icons.LoadingWrapper>
        ) : (
          <Field
            name={`${policy}.isProcessed`}
            // id={`${policy.name}-actions`}
            fullWidth
            onChange={({ target }: { target: any }) => {
              const { value } = target;
              const actionsWithUserAction = [5, 6, 7, 8];

              if (actionsWithUserAction.includes(value)) {
                return actionWithUserAction(value);
              }
              actionWithoutUserAction(value, policyId);
            }}
            options={policyActions}
            disabled={isProcessed}
            component={Fields.Select}
          />
        )}
      </TableComponents.TableCell>
    </TableRow>
  );
};

export default connect(
  (state: any) => ({
    customers: selectors.customersSelectors.customers(state),
    policyTypes: selectors.metaSelectors.policyTypes(state),
    policyTypesRecommended:
      selectors.metaSelectors.policyTypesRecommended(state),
    policyProviders: selectors.metaSelectors.policyProviders(state),
    policyPlatforms: selectors.metaSelectors.policyPlatforms(state)
  }),
  {
    getRecommendationsData: actions.recommendationActions.getRecommendationData,
    changeField: change
  }
)(Policy);
